import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, DollarSign, Euro, Clock, Zap, AlertCircle, CheckCircle, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { LoadingSpinner } from '../../components/LoadingSpinner';

interface BlogInfo {
  id: string;
  title: string;
  published_at: string;
  session_id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  view_count: number;
}

interface APICallDetail {
  id: string;
  created_at: string;
  action_type: string;
  ai_provider: string;
  credits_used: number;
  euro_cost: string;
  success: boolean;
  prompt_type?: string;
  model_name?: string;
  response_metadata?: any;
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  response_time_ms?: number;
  error_message?: string;
}

const PROVIDER_ICONS: Record<string, string> = {
  'OpenAI': '🤖',
  'Leonardo': '🎨',
  'SendGrid': '📧',
};

const ACTION_TYPE_LABELS: Record<string, string> = {
  'idea_generate': 'Idee Genereren',
  'article_generate': 'Artikel Genereren',
  'article_regenerate': 'Artikel Regenereren',
  'titles_generate': 'Titels Genereren',
  'intros_generate': 'Intro\'s Genereren',
  'images_generate': 'Afbeeldingen Genereren',
  'email_variant_generate': 'Email Variant',
  'blog_to_email': 'Blog naar Email',
};

export function AdminBlogCostDetail() {
  const navigate = useNavigate();
  const { blogId } = useParams<{ blogId: string }>();
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState<'EUR' | 'USD'>('EUR');
  const [exchangeRate, setExchangeRate] = useState(1.0);
  const [expandedCalls, setExpandedCalls] = useState<Set<string>>(new Set());

  const [blogInfo, setBlogInfo] = useState<BlogInfo | null>(null);
  const [apiCalls, setApiCalls] = useState<APICallDetail[]>([]);
  const [totalCost, setTotalCost] = useState(0);
  const [costByProvider, setCostByProvider] = useState<Record<string, number>>({});

  useEffect(() => {
    loadAllData();
  }, [blogId]);

  const loadAllData = async () => {
    if (!blogId) return;
    setLoading(true);
    await Promise.all([
      loadBlogInfo(),
      loadAPICallDetails(),
      loadExchangeRate(),
    ]);
    setLoading(false);
  };

  const loadBlogInfo = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-get-data?type=blog-info&blogId=${blogId}`;
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setBlogInfo(result.data);
      }
    } catch (error) {
      console.error('Failed to load blog info:', error);
    }
  };

  const loadAPICallDetails = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-get-data?type=blog-detail-costs&blogId=${blogId}`;
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setApiCalls(result.data || []);

        const total = result.data.reduce((sum: number, call: APICallDetail) =>
          sum + parseFloat(call.euro_cost), 0
        );
        setTotalCost(total);

        const byProvider: Record<string, number> = {};
        result.data.forEach((call: APICallDetail) => {
          if (!byProvider[call.ai_provider]) {
            byProvider[call.ai_provider] = 0;
          }
          byProvider[call.ai_provider] += parseFloat(call.euro_cost);
        });
        setCostByProvider(byProvider);
      }
    } catch (error) {
      console.error('Failed to load API calls:', error);
    }
  };

  const loadExchangeRate = async () => {
    try {
      const { data } = await supabase
        .from('system_config')
        .select('value')
        .eq('key', 'exchange_rate_usd_to_euro')
        .maybeSingle();

      if (data) {
        setExchangeRate(parseFloat(data.value) || 1.0);
      }
    } catch (error) {
      console.error('Failed to load exchange rate:', error);
    }
  };

  const convertCost = (euroCost: number): string => {
    if (isNaN(euroCost)) return '0.0000';
    if (currency === 'USD') {
      return (euroCost / exchangeRate).toFixed(4);
    }
    return euroCost.toFixed(4);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('nl-NL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const toggleExpand = (callId: string) => {
    const newExpanded = new Set(expandedCalls);
    if (newExpanded.has(callId)) {
      newExpanded.delete(callId);
    } else {
      newExpanded.add(callId);
    }
    setExpandedCalls(newExpanded);
  };

  const currencySymbol = currency === 'EUR' ? '€' : '$';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center">
        <LoadingSpinner text="Blog kosten laden..." />
      </div>
    );
  }

  if (!blogInfo) {
    return (
      <div className="min-h-screen bg-[#F2F2EF] p-8">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="btn-secondary mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Terug
          </button>
          <div className="card text-center py-12">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 text-red-500" />
            <p className="text-lg font-medium text-gray-700">Blog niet gevonden</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF] p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="btn-secondary mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Terug
          </button>

          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-[#231F20] mb-2">{blogInfo.title}</h1>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span>Gepubliceerd: {formatDate(blogInfo.published_at)}</span>
                <span>•</span>
                <span>{blogInfo.view_count} views</span>
                <span>•</span>
                <span>Gebruiker: {blogInfo.user_name} ({blogInfo.user_email})</span>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-white rounded-lg p-1 shadow-sm border border-gray-200">
              <button
                onClick={() => setCurrency('EUR')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                  currency === 'EUR'
                    ? 'bg-[#231F20] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Euro className="w-4 h-4" />
                EUR
              </button>
              <button
                onClick={() => setCurrency('USD')}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all ${
                  currency === 'USD'
                    ? 'bg-[#231F20] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <DollarSign className="w-4 h-4" />
                USD
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="text-sm text-gray-600 mb-1">Totale Kosten</div>
            <div className="text-3xl font-bold text-green-700">{currencySymbol}{convertCost(totalCost)}</div>
            <div className="text-xs text-gray-600 mt-2">{apiCalls.length} API calls</div>
          </div>

          {Object.entries(costByProvider).map(([provider, cost]) => (
            <div key={provider} className="card">
              <div className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                <span>{PROVIDER_ICONS[provider]}</span>
                {provider}
              </div>
              <div className="text-2xl font-bold text-[#231F20]">{currencySymbol}{convertCost(cost)}</div>
              <div className="text-xs text-gray-600 mt-2">
                {apiCalls.filter(c => c.ai_provider === provider).length} calls
              </div>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="pb-4 border-b border-gray-200 mb-6">
            <h2 className="text-xl font-bold text-[#231F20]">Individuele API Calls</h2>
            <p className="text-sm text-gray-600 mt-1">
              Gedetailleerde breakdown van alle API calls tijdens het aanmaken van dit blog
            </p>
          </div>

          {apiCalls.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="text-lg font-medium">Geen API calls gevonden</p>
            </div>
          ) : (
            <div className="space-y-4">
              {apiCalls.map((call, index) => {
                const isExpanded = expandedCalls.has(call.id);
                const icon = PROVIDER_ICONS[call.ai_provider] || '⚡';
                const actionLabel = ACTION_TYPE_LABELS[call.action_type] || call.action_type;

                return (
                  <div
                    key={call.id}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors"
                  >
                    <div
                      onClick={() => toggleExpand(call.id)}
                      className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-2xl">{icon}</span>
                            <div>
                              <h3 className="font-semibold text-[#231F20]">
                                {actionLabel}
                                {call.model_name && (
                                  <span className="ml-2 text-sm font-normal text-gray-500">
                                    ({call.model_name})
                                  </span>
                                )}
                              </h3>
                              <div className="flex items-center gap-4 text-xs text-gray-600 mt-1">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatTime(call.created_at)}
                                </span>
                                {call.total_tokens && (
                                  <span className="flex items-center gap-1">
                                    <Zap className="w-3 h-3" />
                                    {call.total_tokens.toLocaleString()} tokens
                                  </span>
                                )}
                                {call.response_time_ms && (
                                  <span>{(call.response_time_ms / 1000).toFixed(2)}s</span>
                                )}
                                <span className={`flex items-center gap-1 ${call.success ? 'text-green-600' : 'text-red-600'}`}>
                                  {call.success ? (
                                    <>
                                      <CheckCircle className="w-3 h-3" />
                                      Success
                                    </>
                                  ) : (
                                    <>
                                      <AlertCircle className="w-3 h-3" />
                                      Failed
                                    </>
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="text-xl font-bold text-green-700">
                              {currencySymbol}{convertCost(parseFloat(call.euro_cost))}
                            </div>
                            {call.credits_used > 0 && (
                              <div className="text-xs text-gray-500">{call.credits_used} credits</div>
                            )}
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-gray-200 bg-gray-50">
                        <div className="mt-4 grid grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Details</h4>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Provider:</span>
                                <span className="font-medium">{call.ai_provider}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Action Type:</span>
                                <span className="font-medium">{call.action_type}</span>
                              </div>
                              {call.prompt_type && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Prompt Type:</span>
                                  <span className="font-medium">{call.prompt_type}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-gray-600">Timestamp:</span>
                                <span className="font-medium">{formatDate(call.created_at)}</span>
                              </div>
                            </div>
                          </div>

                          {call.ai_provider === 'OpenAI' && (call.input_tokens || call.output_tokens) && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 mb-2">Token Usage</h4>
                              <div className="space-y-1 text-sm">
                                {call.input_tokens && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Input Tokens:</span>
                                    <span className="font-medium">{call.input_tokens.toLocaleString()}</span>
                                  </div>
                                )}
                                {call.output_tokens && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Output Tokens:</span>
                                    <span className="font-medium">{call.output_tokens.toLocaleString()}</span>
                                  </div>
                                )}
                                {call.total_tokens && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Total Tokens:</span>
                                    <span className="font-medium font-bold">{call.total_tokens.toLocaleString()}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {call.ai_provider === 'Leonardo' && call.response_metadata && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 mb-2">Image Details</h4>
                              <div className="space-y-1 text-sm">
                                {call.response_metadata.images_count && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Images Generated:</span>
                                    <span className="font-medium">{call.response_metadata.images_count}</span>
                                  </div>
                                )}
                                {call.response_metadata.model && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Model:</span>
                                    <span className="font-medium">{call.response_metadata.model}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        {call.response_metadata && (
                          <div className="mt-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Response Metadata</h4>
                            <div className="bg-white p-3 rounded border border-gray-200 text-xs font-mono overflow-x-auto">
                              <pre className="text-gray-700">
                                {JSON.stringify(call.response_metadata, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}

                        {!call.success && call.error_message && (
                          <div className="mt-4">
                            <h4 className="text-sm font-semibold text-red-700 mb-2">Error Message</h4>
                            <div className="bg-red-50 p-3 rounded border border-red-200 text-sm text-red-800">
                              {call.error_message}
                            </div>
                          </div>
                        )}

                        <div className="mt-4 flex items-center justify-end">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/prompt-logs?search=${call.id}`);
                            }}
                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            Bekijk in AI Logs
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
